import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, Pressable, Image, ScrollView } from 'react-native-web';
import { StyleSheet } from 'react-native-web';
import Modal from "react-modal";
import ReactPlayer from "react-player/lazy";
import Button from './Button';
import xIcon from "../icons/DupeIcons-16-Biz-Unavailable.png";
import photoIcon from "../icons/DupeIcons-14-Photo.png";
import videoIcon from "../icons/DupeIcons-12-Video.png";

const ContentModal = ({
    isOpen,
    onClose,
    selectedCreator,
    creatorsUploads,
    campaign,
    creatorPaymentsAllowed,
    showTransferError,
    confirmPhotos,
    onAcceptContent,
    onSetConfirmPhotos
}) => {
    const [selectedContent, setSelectedContent] = useState(null);
    const [currentContentIndex, setCurrentContentIndex] = useState(0);
    const [allContent, setAllContent] = useState([]);

    // Add CSS for video styling
    const videoStyles = `
        .content-modal-video {
            border-radius: 8px !important;
            margin: 0;
            padding: 0;
            display: block;
            position: relative;
            width: 100%;
            height: 100%;
        }
        
        .content-modal-video video {
            border-radius: 8px !important;
            margin: 0 !important;
            padding: 0 !important;
            display: block;
            position: relative;
            width: 100% !important;
            height: 100% !important;
            object-fit: cover !important;
        }
        
        .content-modal-video > div {
            border-radius: 8px !important;
            overflow: hidden;
            margin: 0 !important;
            padding: 0 !important;
            position: relative;
            width: 100% !important;
            height: 100% !important;
        }
        
        .content-modal-selected-video {
            border-radius: 8px !important;
            overflow: hidden;
            margin: 0;
            padding: 0;
            display: flex;
            justify-content: center;
            position: relative;
            max-width: 600px;
            margin: 0 auto;
        }
        
        .content-modal-selected-video video {
            border-radius: 8px !important;
            margin: 0 !important;
            padding: 0 !important;
            display: block;
            position: relative;
            width: auto !important;
            height: 100% !important;
            object-fit: contain !important;
        }
        
        .content-modal-selected-video > div {
            border-radius: 8px !important;
            overflow: hidden;
            margin: 0 !important;
            padding: 0 !important;
            position: relative;
            width: auto !important;
        }
    `;

    // Inject styles into head
    React.useEffect(() => {
        const styleSheet = document.createElement("style");
        styleSheet.innerText = videoStyles;
        document.head.appendChild(styleSheet);
        return () => document.head.removeChild(styleSheet);
    }, []);

    // Helper function to create unified content array
    function createUnifiedContentArray(uploads) {
        const photos = (uploads.photo_uploads || []).map(item => ({
            ...item,
            type: 'photo',
            url: `https://d3p3fw3rutb1if.cloudfront.net/business/uploads/${item.img_preview_id}`,
            content_type: 'PHOTO'
        }));
        const videos = (uploads.video_uploads || []).map(item => ({
            ...item,
            type: 'video',
            url: `https://d3p3fw3rutb1if.cloudfront.net/business/uploads/${item.img_preview_id}`,
            content_type: 'VIDEO'
        }));
        return [...photos, ...videos];
    }

    // Helper function to find content index
    function findContentIndex(content, allContentArray) {
        return allContentArray.findIndex(item => 
            item.img_preview_id === content.img_preview_id && 
            item.type === (content.content_type === 'PHOTO' ? 'photo' : 'video')
        );
    }

    // Navigation functions
    function navigateToContent(index) {
        if (index >= 0 && index < allContent.length) {
            setCurrentContentIndex(index);
            setSelectedContent(allContent[index]);
        }
    }

    function navigateToPrevious() {
        const newIndex = currentContentIndex > 0 ? currentContentIndex - 1 : allContent.length - 1;
        navigateToContent(newIndex);
    }

    function navigateToNext() {
        const newIndex = currentContentIndex < allContent.length - 1 ? currentContentIndex + 1 : 0;
        navigateToContent(newIndex);
    }

    // Keyboard navigation handler
    function handleKeyNavigation(event) {
        if (!selectedContent) return;
        
        switch (event.key) {
            case 'ArrowLeft':
                event.preventDefault();
                navigateToPrevious();
                break;
            case 'ArrowRight':
                event.preventDefault();
                navigateToNext();
                break;
            case 'Escape':
                event.preventDefault();
                setSelectedContent(null);
                break;
        }
    }

    // Add keyboard navigation when content modal is open
    useEffect(() => {
        if (isOpen && selectedContent) {
            document.addEventListener('keydown', handleKeyNavigation);
            return () => {
                document.removeEventListener('keydown', handleKeyNavigation);
            };
        }
    }, [isOpen, selectedContent, currentContentIndex, allContent]);

    // Initialize content when modal opens or uploads change
    useEffect(() => {
        if (isOpen && creatorsUploads) {
            const unified = createUnifiedContentArray(creatorsUploads);
            setAllContent(unified);
            setCurrentContentIndex(0);
            setSelectedContent(null);
        }
    }, [isOpen, creatorsUploads]);

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedContent(null);
            setCurrentContentIndex(0);
            setAllContent([]);
        }
    }, [isOpen]);

    function handleContentClick(content, contentType) {
        const contentWithType = { ...content, content_type: contentType };
        const contentIndex = findContentIndex(contentWithType, allContent);
        setCurrentContentIndex(contentIndex);
        setSelectedContent(contentWithType);
    }

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            className={"invite-modal"}
            style={styles.modal}
        >
            <View style={styles.contentModalContainer}>
                {confirmPhotos && (
                    <ScrollView
                        style={styles.contentModalScrollView}
                        showsVerticalScrollIndicator={true}
                        bounces={false}
                    >
                        <View style={styles.contentModalBody}>
                            <View style={styles.contentModalHeader}>
                                <View style={styles.contentModalHeaderLeft}>
                                    <Text style={styles.contentModalTitle}>Creator Uploads</Text>
                                    <Text style={styles.creatorNameSubtitle}>
                                        {selectedCreator?.first_name} {selectedCreator?.last_name}
                                    </Text>
                                </View>
                                <TouchableOpacity onPress={onClose} style={styles.closeButton}>
                                    <Image source={xIcon} style={styles.closeIcon} />
                                </TouchableOpacity>
                            </View>

                            <View style={styles.contentModalLayout}>
                                <View style={styles.deliverablesSidebar}>
                                    <Text style={styles.deliverablesTitle}>Deliverables</Text>
                                    {creatorsUploads?.photo_uploads && (
                                        <View style={styles.deliverableItem}>
                                            <View style={styles.deliverableIcon}>
                                                <Image source={photoIcon} style={styles.deliverableIconImage} />
                                            </View>
                                            <Text style={styles.deliverableText}>
                                                {creatorsUploads.photo_uploads.length} Photo{creatorsUploads.photo_uploads.length !== 1 ? 's' : ''}
                                            </Text>
                                        </View>
                                    )}
                                    {creatorsUploads?.video_uploads && (
                                        <View style={styles.deliverableItem}>
                                            <View style={styles.deliverableIcon}>
                                                <Image source={videoIcon} style={styles.deliverableIconImage} />
                                            </View>
                                            <Text style={styles.deliverableText}>
                                                {creatorsUploads.video_uploads.length} Video{creatorsUploads.video_uploads.length !== 1 ? 's' : ''}
                                            </Text>
                                        </View>
                                    )}
                                </View>

                                {!selectedContent && (
                                    <View style={styles.contentGallery}>
                                        {creatorsUploads?.photo_uploads && creatorsUploads.photo_uploads.length > 0 && (
                                            <View style={styles.contentSection}>
                                                <Text style={styles.contentSectionTitle}>Photos</Text>
                                                <View style={styles.contentGrid}>
                                                    {creatorsUploads.photo_uploads.map((photo, index) => (
                                                        <Pressable
                                                            key={index}
                                                            onPress={() => handleContentClick(photo, 'PHOTO')}
                                                            style={styles.contentGridItem}
                                                        >
                                                            <Image
                                                                source={{uri: "https://d3p3fw3rutb1if.cloudfront.net/business/uploads/" + photo.img_preview_id}}
                                                                style={styles.contentGridImage}
                                                                resizeMode="cover"
                                                            />
                                                        </Pressable>
                                                    ))}
                                                </View>
                                            </View>
                                        )}
                                        {creatorsUploads?.video_uploads && creatorsUploads.video_uploads.length > 0 && (
                                            <View style={styles.contentSection}>
                                                <Text style={styles.contentSectionTitle}>Videos</Text>
                                                <View style={styles.contentGrid}>
                                                    {creatorsUploads.video_uploads.map((video, index) => (
                                                        <Pressable
                                                            key={index}
                                                            onPress={() => handleContentClick(video, 'VIDEO')}
                                                            style={styles.contentGridItem}
                                                        >
                                                            <View style={styles.videoThumbnailContainer}>
                                                                <ReactPlayer
                                                                    width="100%"
                                                                    height="100%"
                                                                    className="content-modal-video"
                                                                    url={"https://d3p3fw3rutb1if.cloudfront.net/business/uploads/" + video.img_preview_id}
                                                                    muted={true}
                                                                    playing={true}
                                                                    loop={true}
                                                                    controls={false}
                                                                />
                                                            </View>
                                                        </Pressable>
                                                    ))}
                                                </View>
                                            </View>
                                        )}
                                    </View>
                                )}

                                {selectedContent && (
                                    <View style={styles.selectedContentView}>
                                        <View style={styles.selectedContentHeader}>
                                            <TouchableOpacity onPress={() => setSelectedContent(null)} style={styles.backButton}>
                                                <Text style={styles.backButtonText}>← Back to Gallery</Text>
                                            </TouchableOpacity>
                                            <View style={styles.contentCounter}>
                                                <Text style={styles.contentCounterText}>
                                                    {currentContentIndex + 1} of {allContent.length}
                                                </Text>
                                                <Text style={styles.keyboardHintText}>
                                                    Use ← → keys to navigate
                                                </Text>
                                            </View>
                                        </View>
                                        <View style={styles.selectedContentContainer}>
                                            {/* Previous Arrow */}
                                            <Pressable 
                                                style={({ hovered }) => [
                                                    styles.navigationArrow, 
                                                    styles.previousArrow,
                                                    hovered && styles.navigationArrowHovered
                                                ]}
                                                onPress={navigateToPrevious}
                                                accessible={true}
                                                accessibilityLabel="Previous content"
                                                accessibilityRole="button"
                                            >
                                                <Text style={styles.arrowText}>‹</Text>
                                            </Pressable>

                                            {/* Content Display */}
                                            <View style={styles.contentDisplayArea}>
                                                {selectedContent.content_type === "PHOTO" && (
                                                    <img
                                                        className="selected-content-image"
                                                        src={"https://d3p3fw3rutb1if.cloudfront.net/business/uploads/" + selectedContent.img_preview_id}
                                                        alt="Selected content"
                                                        style={{
                                                            maxWidth: '100%',
                                                            maxHeight: '400px',
                                                            height: 'auto',
                                                            width: 'auto',
                                                            borderRadius: '8px',
                                                            objectFit: 'contain'
                                                        }}
                                                    />
                                                )}
                                                {selectedContent.content_type === "VIDEO" && (
                                                    <ReactPlayer
                                                        width="100%"
                                                        height="400px"
                                                        className="content-modal-selected-video"
                                                        url={"https://d3p3fw3rutb1if.cloudfront.net/business/uploads/" + selectedContent.img_preview_id}
                                                        muted={false}
                                                        playing={true}
                                                        loop={true}
                                                        controls={true}
                                                    />
                                                )}
                                            </View>

                                            {/* Next Arrow */}
                                            <Pressable 
                                                style={({ hovered }) => [
                                                    styles.navigationArrow, 
                                                    styles.nextArrow,
                                                    hovered && styles.navigationArrowHovered
                                                ]}
                                                onPress={navigateToNext}
                                                accessible={true}
                                                accessibilityLabel="Next content"
                                                accessibilityRole="button"
                                            >
                                                <Text style={styles.arrowText}>›</Text>
                                            </Pressable>
                                        </View>
                                    </View>
                                )}
                            </View>

                            {selectedCreator?.stage !== "COMPLETE" && (
                                <View style={styles.contentModalFooter}>
                                    <Button variant={"secondary"} text={"Cancel"} onPress={onClose} />
                                    {campaign?.paymentCompensation && campaign?.defaultPayment > 0 && (
                                        <Button text={"Accept Content"} onPress={() => onSetConfirmPhotos(false)} />
                                    )}
                                    {(!campaign?.paymentCompensation || !campaign?.defaultPayment) && (
                                        <Button text={"Mark As Complete"} onPress={onAcceptContent} />
                                    )}
                                </View>
                            )}
                        </View>
                    </ScrollView>
                )}
                {!confirmPhotos && (
                    <ScrollView
                        style={styles.contentModalScrollView}
                        showsVerticalScrollIndicator={true}
                        bounces={false}
                    >
                        <View style={styles.contentModalBody}>
                            <View style={styles.contentModalHeader}>
                                <View style={styles.contentModalHeaderLeft}>
                                    <Text style={styles.contentModalTitle}>Confirm Payment for Content</Text>
                                    <Text style={styles.creatorNameSubtitle}>
                                        You will be sending {selectedCreator?.first_name} {selectedCreator?.last_name} the amount of ${campaign?.defaultPayment}
                                    </Text>
                                </View>
                                <TouchableOpacity onPress={onClose} style={styles.closeButton}>
                                    <Image source={xIcon} style={styles.closeIcon} />
                                </TouchableOpacity>
                            </View>

                            <View style={styles.paymentSummaryContainer}>
                                <Text style={styles.paymentSummaryTitle}>Payment Summary</Text>
                                {creatorsUploads?.photo_uploads && (
                                    <View style={styles.paymentSummaryItem}>
                                        <Text style={styles.paymentSummaryText}>{creatorsUploads.photo_uploads.length} Photo Upload(s)</Text>
                                    </View>
                                )}
                                {creatorsUploads?.video_uploads && (
                                    <View style={styles.paymentSummaryItem}>
                                        <Text style={styles.paymentSummaryText}>{creatorsUploads.video_uploads.length} Video Upload(s)</Text>
                                    </View>
                                )}

                                {showTransferError && (
                                    <View style={styles.errorContainer}>
                                        <Text style={styles.errorText}>You have insufficient funds. Please deposit more funds on the account page.</Text>
                                    </View>
                                )}
                                {!creatorPaymentsAllowed && (
                                    <View style={styles.errorContainer}>
                                        <Text style={styles.errorText}>This creator has not finished setting up their Stripe account to accept payments. Ask them to reach out to Dupe if they are having trouble.</Text>
                                    </View>
                                )}
                            </View>

                            <View style={styles.contentModalFooter}>
                                <Button variant={"secondary"} text={"Cancel"} onPress={onClose} />
                                {creatorPaymentsAllowed && (
                                    <Button text={"Submit Payment"} onPress={onAcceptContent}/>
                                )}
                            </View>
                        </View>
                    </ScrollView>
                )}
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    modal: {
        overlay: {
            display: 'flex',
            flex: 1,
            justifyContent: 'center',
            alignContent: 'center',
            backgroundColor: 'rgba(57, 109, 224, .5)'
        },
        content: {
            margin: '20%',
            inset: 0,
            background: 'none',
            flex: 1,
            border: 'none',
            alignSelf: 'center',
            alignContent: 'center',
            outline: 'none',
        }
    },
    contentModalContainer: {
        backgroundColor: 'white',
        borderRadius: 15,
        padding: 0,
        maxHeight: '90vh',
        maxWidth: '90vw',
        width: 900,
        overflow: 'hidden',
    },
    contentModalBody: {
        padding: 25,
    },
    contentModalHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 25,
        paddingBottom: 15,
        borderBottomWidth: 1,
        borderBottomColor: '#b8cbf5',
    },
    contentModalHeaderLeft: {
        flexDirection: 'column',
    },
    closeButton: {
        padding: 8,
        borderRadius: 20,
        backgroundColor: '#f8f8f8',
    },
    closeIcon: {
        width: 18,
        height: 18,
    },
    contentModalTitle: {
        fontFamily: "'forma-djr-display', sans-serif",
        fontSize: 28,
        fontWeight: 'bold',
        color: '#396de0',
        marginBottom: 5,
    },
    creatorNameSubtitle: {
        fontFamily: "'lato', sans-serif",
        fontSize: 16,
        color: '#737373',
        fontWeight: '500',
    },
    contentModalLayout: {
        flexDirection: 'row',
        minHeight: 500,
    },
    deliverablesSidebar: {
        width: 200,
        paddingRight: 25,
        borderRightWidth: 1,
        borderRightColor: '#f0f0f0',
    },
    deliverablesTitle: {
        fontFamily: "'forma-djr-display', sans-serif",
        fontSize: 18,
        fontWeight: 'bold',
        color: '#396de0',
        marginBottom: 15,
    },
    deliverableItem: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 12,
        padding: 8,
        backgroundColor: '#f8f9ff',
        borderRadius: 8,
    },
    deliverableIcon: {
        width: 24,
        height: 24,
        marginRight: 10,
        backgroundColor: '#396de0',
        borderRadius: 4,
        alignItems: 'center',
        justifyContent: 'center',
        padding: 4,
    },
    deliverableIconImage: {
        width: 16,
        height: 16,
        tintColor: 'white',
    },
    deliverableText: {
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
        color: '#396de0',
        fontWeight: '600',
    },
    contentGallery: {
        flex: 1,
        paddingLeft: 25,
    },
    contentSection: {
        marginBottom: 25,
    },
    contentSectionTitle: {
        fontFamily: "'forma-djr-display', sans-serif",
        fontSize: 18,
        fontWeight: 'bold',
        color: '#396de0',
        marginBottom: 15,
    },
    contentGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 10,
    },
    contentGridItem: {
        width: 120,
        height: 120,
        borderRadius: 8,
        overflow: 'hidden',
        borderWidth: 2,
        borderColor: 'transparent',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    contentGridImage: {
        width: '100%',
        height: '100%',
        borderRadius: 6,
    },
    videoThumbnailContainer: {
        position: 'relative',
        width: '100%',
        height: '100%',
    },
    selectedContentView: {
        flex: 1,
        paddingLeft: 25,
    },
    selectedContentHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 15,
    },
    backButton: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 8,
    },
    backButtonText: {
        fontFamily: "'lato', sans-serif",
        fontSize: 16,
        color: '#396de0',
        fontWeight: '600',
    },
    contentCounter: {
        backgroundColor: '#f3f4f6',
        borderRadius: 20,
        paddingVertical: 6,
        paddingHorizontal: 12,
    },
    contentCounterText: {
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
        color: '#6b7280',
        fontWeight: '500',
    },
    keyboardHintText: {
        fontFamily: "'lato', sans-serif",
        fontSize: 12,
        color: '#9ca3af',
        fontWeight: '400',
        marginTop: 2,
        textAlign: 'center',
    },
    selectedContentContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8f8f8',
        borderRadius: 10,
        padding: 20,
        minHeight: 400,
        width: '100%',
        position: 'relative',
    },
    contentDisplayArea: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        marginHorizontal: 20,
    },
    navigationArrow: {
        width: 75,
        height: 75,
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        zIndex: 10,
        outlineStyle: 'none',
        borderWidth: 0,
    },
    previousArrow: {
        marginRight: 10,
    },
    nextArrow: {
        marginLeft: 10,
    },
    arrowText: {
        fontSize: 48,
        color: '#396de0',
        fontWeight: 'bold',
        lineHeight: 48,
        textAlign: 'center',
    },
    navigationArrowHovered: {
        transform: [{ scale: 1.2 }],
    },
    selectedContentVideo: {
        borderRadius: 8,
        overflow: 'hidden',
    },
    contentModalFooter: {
        flexDirection: 'row',
        justifyContent: 'center',
        gap: 15,
        marginTop: 25,
        paddingTop: 20,
        borderTopWidth: 1,
        borderTopColor: '#f0f0f0',
    },
    paymentSummaryContainer: {
        marginBottom: 20,
    },
    paymentSummaryTitle: {
        fontFamily: "'forma-djr-display', sans-serif",
        fontSize: 18,
        fontWeight: 'bold',
        color: '#396de0',
        marginBottom: 10,
    },
    paymentSummaryItem: {
        marginBottom: 5,
    },
    paymentSummaryText: {
        fontFamily: "'lato', sans-serif",
        fontSize: 16,
        color: '#396de0',
    },
    errorContainer: {
        marginBottom: 10,
        padding: 10,
        backgroundColor: '#f8f8f8',
        borderRadius: 5,
        borderWidth: 1,
        borderColor: '#e8b5b5',
    },
    errorText: {
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
        color: '#e8b5b5',
    },
    contentModalScrollView: {
        flex: 1,
    },
});

export default ContentModal; 